import config from '../config'

export interface DatasetProjectActions {
  view?: boolean
  build?: boolean
  download?: boolean
  delete?: boolean
  create?: boolean
}

export interface DatasetProjectNode {
  id: number
  name: string
  parent_id?: number | null
  kind: 'project' | 'task' | 'dataset'
  parent_kind?: 'project' | 'task'
  // 버전 계보상 이전 버전 (None = 독립 데이터셋). 모델 소속과는 다른 개념
  parent_node_id?: number | null
  description?: string | null
  created_at?: string
  actions?: DatasetProjectActions
}

/** 파일 단위 diff — 매니페스트 md5 비교 결과 */
export interface DatasetFileDiffPart {
  image_count: number
  label_count: number
  image_samples?: string[]
  label_samples?: string[]
}

export interface DatasetFileDiff {
  added: DatasetFileDiffPart
  removed: DatasetFileDiffPart
  modified: DatasetFileDiffPart
  base_total?: number
  target_total?: number
}

/** 라벨 단위 diff — 포맷마다 집계 단위가 달라 종류별로 분리된다 */
export interface DatasetLabelDiffSection {
  label_kind: 'yolo' | 'voc' | 'coco' | 'mask'
  unit: 'annotations' | 'pixels'
  per_class: { class: string; raw_key: string; base: number; target: number; delta: number }[]
  totals: { base: number; target: number; delta: number }
}

export interface DatasetLabelDiff {
  sections: DatasetLabelDiffSection[]
  primary_kind?: string | null
  files: { changed: number; parsed: number; unreadable: number; truncated: boolean; max_files: number }
}

export interface DatasetCompareResponse {
  base: { id: number; name: string; content_hash?: string | null }
  target: { id: number; name: string; content_hash?: string | null }
  diff?: DatasetFileDiff | null
  label_diff_status: 'none' | 'queued' | 'running' | 'completed' | 'failed'
  label_diff?: DatasetLabelDiff | null
  reason?: string | null
}

/** 빌드 전에 "비식별 처리가 안 된 사진이 몇 장인가" (백엔드 /datasets/build/preflight) */
export interface AnonymizationPreflight {
  total: number            // 라벨이 붙은 사진 수
  stamped: number          // 비식별 처리 완료로 기록된 것
  skipped: number          // 업로드할 때 비식별을 끈 것 — 승인하면 넣을 수 있다
  blocked: number          // 격리·실패·기록없음 — 승인해도 못 넣는다
  blocked_reasons: Record<string, number>
  needs_approval: boolean
  buildable: number        // 승인했을 때 들어갈 총 장수
}

export interface BuildDatasetResponse {
  job_id?: string | number
  ready?: boolean
  download_url?: string
}

export interface DatasetSummaryResponse {
  job_id: number
  created_at: string
  zip_ready: boolean
  zip_expires_at?: string
  zip_size?: number
  /** ZIP 이 만들어진 뒤 보관되는 일수(서버 설정). ZIP 이 없을 때 안내에 쓴다 */
  zip_retention_days?: number
  total: number
  train: number
  val: number
  test: number
  sampling: any
  output: any
  per_class_counts?: any
  label_set_id: number
  label_version_name?: string
  label_version_path?: string
  label_format?: string
  created_by_id?: number
  created_by_email?: string
  created_by_name?: string
  label_archive_name?: string
  raw_sources: Array<{ node_id: number; node_name?: string; node_path?: string; batch_id?: number; batch_filename?: string }>
}

/** 실패 응답을 **사람이 읽을 오류**로 바꾼다.
 *
 * 서버 본문을 그대로 던지면 화면에 JSON 덩어리가 뜬다 (2026-09-07 에 실제로
 * 그렇게 떴다). detail 이 문장이면 그것을, 객체면 그 안의 message 를, 아무것도
 * 못 읽으면 기본 문장을 쓴다. 원문은 콘솔에만 남긴다.
 *
 * 파싱한 본문은 err.body 에 실어 호출부가 사유 코드·개수를 쓸 수 있게 한다.
 */
async function toReadableError(resp: Response, fallback: string): Promise<Error & { body?: any; code?: string }> {
  let txt = ''
  try { txt = await resp.text() } catch {}
  let body: any = null
  try { body = txt ? JSON.parse(txt) : null } catch {}
  if (txt) console.warn(`요청 실패 ${resp.status}`, txt)
  let message = ''
  const d = body?.detail
  if (typeof d === 'string' && d.trim()) message = d
  else if (d && typeof d === 'object' && typeof d.message === 'string') message = d.message
  else if (typeof body?.message === 'string') message = body.message
  const err = new Error(message || `${fallback} (오류 ${resp.status})`) as Error & { body?: any; code?: string }
  err.body = body
  const code = body?.code ?? (d && typeof d === 'object' ? d.code : undefined)
  if (typeof code === 'string') err.code = code
  return err
}

class DatasetService {
  private baseUrl = config.API_BASE_URL

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('access_token')
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  }

  async listProjects(): Promise<DatasetProjectNode[]> {
    const resp = await fetch(`${this.baseUrl}/datasets/projects`, { headers: this.getAuthHeaders() })
    if (!resp.ok) return []
    return resp.json()
  }

  /** 프로젝트(1단계) 생성. 데이터셋은 작업(2단계)을 거쳐야 하므로 여기서 만들지 않는다. */
  async createProject(data: { projectName: string; projectDescription?: string; existingProjectId?: number }): Promise<{ project_id: number }> {
    // 기존 프로젝트를 선택한 경우 새로 만들지 않고 그대로 사용
    if (data.existingProjectId) {
      return { project_id: data.existingProjectId }
    }
    const resp = await fetch(`${this.baseUrl}/datasets/projects`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        name: data.projectName,
        project_description: data.projectDescription,
      })
    })
    if (!resp.ok) {
      throw await toReadableError(resp, '프로젝트 생성에 실패했습니다.')
    }
    return resp.json()
  }

  async createTask(projectId: number, data: { name: string; description?: string }): Promise<{ task_id: number }> {
    const resp = await fetch(`${this.baseUrl}/datasets/projects/${projectId}/tasks`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        name: data.name,
        description: data.description,
      })
    })
    if (!resp.ok) {
      throw await toReadableError(resp, '작업 생성에 실패했습니다.')
    }
    return resp.json()
  }

  async createDatasetNode(projectId: number, data: { name: string; taskId: number; description?: string }): Promise<{ dataset_id: number }> {
    const resp = await fetch(`${this.baseUrl}/datasets/projects/${projectId}/datasets`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        dataset_name: data.name,
        dataset_description: data.description,
        task_id: data.taskId,
      })
    })
    if (!resp.ok) {
      throw await toReadableError(resp, '데이터셋 생성에 실패했습니다.')
    }
    return resp.json()
  }

  async buildDataset(payload: any): Promise<BuildDatasetResponse> {
    // 백엔드 스키마에 맞춰 snake_case로 변환
    const body = {
      dataset_node_id: payload.dataset_node_id ?? payload.datasetNodeId,
      label_version_node_id: payload.label_version_node_id ?? payload.labelVersionNodeId,
      label_version_node_ids: payload.label_version_node_ids ?? payload.labelVersionNodeIds,
      label_set_id: payload.label_set_id ?? payload.labelSetId,
      sampling: payload.sampling,
      output: payload.output,
      // "이전 버전" 선언 — 제출값이 최종 상태 (null = 계보 없음)
      parent_node_id: payload.parent_node_id ?? payload.parentNodeId ?? null,
      // true면 서버가 같은 모델 안의 직전 데이터셋을 자동 선택
      parent_auto: payload.parent_auto ?? payload.parentAuto ?? false,
      // 비식별 처리를 끄고 올린 사진을 넣겠다는 사용자 승인
      allow_unanonymized: payload.allow_unanonymized ?? payload.allowUnanonymized ?? false,
    }
    const resp = await fetch(`${this.baseUrl}/datasets/build`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(body)
    })
    if (!resp.ok) {
      // **응답 원문을 사용자에게 보여주지 않는다.** 예전에는 본문을 그대로 alert 에
      // 띄워서 JSON 덩어리가 화면에 떴다 (2026-09-07). 사람이 읽을 문장만 꺼내고,
      // 사유 코드·개수는 화면이 안내를 만들 수 있게 오류 객체에 붙여 올린다.
      const err: any = await toReadableError(resp, '데이터셋 생성 요청에 실패했습니다.')
      // 비식별 안 된 사진 승인 필요 등 — 화면이 개수를 보고 안내를 만든다
      err.preflight = err.body?.preflight ?? err.body?.detail?.preflight
      throw err
    }
    return resp.json()
  }

  async buildPreflight(payload: any): Promise<AnonymizationPreflight> {
    const body = {
      dataset_node_id: payload.dataset_node_id ?? payload.datasetNodeId,
      // 아직 노드가 없는 "새로 만들기" 흐름 — 프로젝트로 물어본다
      dataset_project_id: payload.dataset_project_id ?? payload.datasetProjectId,
      label_version_node_id: payload.label_version_node_id ?? payload.labelVersionNodeId,
      label_version_node_ids: payload.label_version_node_ids ?? payload.labelVersionNodeIds,
      label_set_id: payload.label_set_id ?? payload.labelSetId,
      sampling: payload.sampling ?? {},
      output: payload.output ?? {},
    }
    const resp = await fetch(`${this.baseUrl}/datasets/build/preflight`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(body),
    })
    if (!resp.ok) throw await toReadableError(resp, '비식별 상태를 확인하지 못했습니다.')
    return resp.json()
  }

  async getJobStatus(jobId: string | number): Promise<{ status: string; step?: string; progress?: number; download_url?: string; error?: string }> {
    const resp = await fetch(`${this.baseUrl}/datasets/jobs/${jobId}/status`, { headers: this.getAuthHeaders() })
    if (!resp.ok) throw new Error(`상태 조회 실패(${resp.status})`)
    return resp.json()
  }

  async getLatestSummary(datasetNodeId: number): Promise<{ ready: boolean; zip_ready: boolean; zip_expires_at?: string; zip_size?: number; download_url?: string; reason?: 'none' | 'lost' | 'building' | 'restore_failed' }> {
    const resp = await fetch(`${this.baseUrl}/datasets/${datasetNodeId}/latest`, { headers: this.getAuthHeaders() })
    if (!resp.ok) return { ready: false, zip_ready: false }
    return resp.json()
  }

  async getSummary(datasetNodeId: number): Promise<DatasetSummaryResponse> {
    const resp = await fetch(`${this.baseUrl}/datasets/${datasetNodeId}/summary`, { headers: this.getAuthHeaders() })
    if (!resp.ok) {
      throw await toReadableError(resp, '요약 조회에 실패했습니다.')
    }
    return resp.json()
  }

  /**
   * ZIP 다운로드. 준비돼 있으면 브라우저 기본 다운로드에 맡긴다.
   *
   * 예전에는 fetch 로 받아 blob 으로 바꿨는데, 1GB 넘는 ZIP 이 브라우저 메모리에
   * 통째로 올라가 "Failed to fetch" 로 죽었다. 대용량은 <a href> 로 넘겨야
   * 브라우저가 디스크로 바로 흘려보내고 자체 진행률도 보여준다.
   * 헤더를 붙일 수 없으므로 access_token 쿼리로 인증한다 (원천데이터와 동일).
   *
   * 먼저 prepare_only 요청으로 준비 상태만 확인한다 — 202(준비 중) 또는
   * 작은 ready JSON만 받고 ZIP 본문은 받지 않는다.
   */
  async download(datasetNodeId: number, preferredName?: string): Promise<{ status: 'ok' } | { status: 'processing'; message?: string; polling_url?: string }> {
    const token = localStorage.getItem('access_token') || ''
    const name = preferredName?.trim()
    const prepareParams = new URLSearchParams({ prepare_only: 'true' })
    if (name) prepareParams.set('filename', name)

    // 준비 여부 확인 — 준비 완료여도 1GB ZIP 대신 작은 JSON만 응답한다.
    const probe = await fetch(
      `${this.baseUrl}/datasets/${datasetNodeId}/download?${prepareParams.toString()}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
    if (probe.status === 202) {
      const data = await probe.json().catch(() => ({} as any))
      return {
        status: 'processing',
        message: data?.message || '보관된 데이터셋으로 ZIP 파일을 만들고 있습니다.',
        polling_url: data?.polling_url || `/datasets/${datasetNodeId}/restore-status`,
      }
    }
    if (!probe.ok) {
      const raw = (await probe.text()).trim()
      let detail = raw
      try {
        const parsed = JSON.parse(raw)
        if (typeof parsed?.detail === 'string') detail = parsed.detail
        else if (typeof parsed?.message === 'string') detail = parsed.message
      } catch {
        // JSON 이 아니면 본문을 그대로 보여준다
      }
      throw new Error(detail || `다운로드 실패(${probe.status})`)
    }

    // 준비 완료 → 브라우저에 맡긴다 (JS 메모리를 쓰지 않는다)
    const params = new URLSearchParams()
    if (name) params.set('filename', name)
    if (token) params.set('access_token', token)
    const a = document.createElement('a')
    a.href = `${this.baseUrl}/datasets/${datasetNodeId}/download?${params.toString()}`
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
    return { status: 'ok' }
  }

  /**
   * 재조립(복원) 진행 상태 폴링 — 202 응답의 polling_url 사용
   *
   * progress 는 0~99 (완료는 status='ready' 로 알린다). 서버가 아직 총량을
   * 모르는 극초반에는 없을 수 있으므로 optional 이다.
   */
  async checkRestoreStatus(pollingUrl: string): Promise<{ status: 'ready' | 'processing' | 'failed'; message?: string; progress?: number }> {
    const url = pollingUrl.startsWith('http') ? pollingUrl : `${this.baseUrl}${pollingUrl}`
    const resp = await fetch(url, { headers: this.getAuthHeaders() })
    if (!resp.ok) return { status: 'processing' }
    const data = await resp.json()
    const status = data.status === 'ready' ? 'ready' : data.status === 'failed' ? 'failed' : 'processing'
    const progress = typeof data.progress === 'number' ? data.progress : undefined
    return { status, message: data.message, progress }
  }

  /** 생성 시 선언된 "이전 버전"과의 관계·차이 */
  async getLineage(datasetNodeId: number): Promise<{
    parent: { id: number; name: string } | null
    diff: {
      added: { image_count: number; label_count: number }
      removed: { image_count: number; label_count: number }
      modified: { image_count: number; label_count: number }
    } | null
    reason?: string
  } | null> {
    const resp = await fetch(`${this.baseUrl}/datasets/${datasetNodeId}/lineage`, { headers: this.getAuthHeaders() })
    if (!resp.ok) return null
    return resp.json()
  }

  /** 이 버전의 클래스별 분포 (모든 라벨 포맷). 무거우면 queued 로 먼저 온다. */
  async getClassStats(datasetNodeId: number): Promise<{
    status: 'none' | 'queued' | 'running' | 'completed' | 'failed'
    content_hash?: string
    sections: { label_kind: string; unit: 'annotations' | 'pixels'; total: number;
                per_class: { class: string; raw_key: string; count: number; ratio: number }[] }[]
    files?: { labels: number; parsed: number; unreadable: number; truncated: boolean; max_files: number }
    reason?: string | null
  } | null> {
    const resp = await fetch(`${this.baseUrl}/datasets/${datasetNodeId}/class-stats`, { headers: this.getAuthHeaders() })
    if (!resp.ok) return null
    return resp.json()
  }

  /** 임의의 두 버전 비교. 라벨 단위 diff 는 백그라운드 계산이라 queued 로 먼저 올 수 있다. */
  async compareVersions(datasetNodeId: number, baseNodeId: number): Promise<DatasetCompareResponse | null> {
    const resp = await fetch(
      `${this.baseUrl}/datasets/${datasetNodeId}/compare?base_node_id=${baseNodeId}`,
      { headers: this.getAuthHeaders() },
    )
    if (!resp.ok) return null
    return resp.json()
  }

  /** 학습데이터셋 프로젝트 삭제 (관리자 전용). 하위 모델·데이터셋이 함께 삭제된다. */
  async deleteProject(projectId: number): Promise<{ deleted_nodes?: number }> {
    const resp = await fetch(`${this.baseUrl}/datasets/projects/${projectId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    })
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}))
      throw new Error(err.detail || '프로젝트 삭제에 실패했습니다')
    }
    return resp.json()
  }

  async deleteDataset(datasetNodeId: number): Promise<void> {
    const resp = await fetch(`${this.baseUrl}/datasets/${datasetNodeId}` , {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    })
    if (!resp.ok) {
      try {
        const ct = resp.headers.get('content-type') || ''
        if (ct.includes('application/json')) {
          const data = await resp.json()
          const msg = (data && (data.detail || data.message)) || (typeof data === 'string' ? data : '')
          if (msg && String(msg).trim().length > 0) throw new Error(String(msg))
        }
      } catch {}
      try {
        throw await toReadableError(resp, '삭제에 실패했습니다.')
      } catch {
        throw new Error(`삭제 실패(${resp.status})`)
      }
    }
  }
}

export default new DatasetService()
